/**
 * Customer Promoter Dashboard
 * ─────────────────────────────────────────────────────────────────────────
 * Route: /account/promoter
 *
 * Renders only if the logged-in customer is a promoter (`GET /me` succeeds);
 * otherwise shows a friendly "Become a promoter" CTA.
 *
 * Layout:
 *   1. Top metric strip — Available / Pending / Locked / Lifetime / Guests
 *   2. Referral link card (copy-to-clipboard + share)
 *   3. 30-day revenue & commission line chart (recharts)
 *   4. Wallet ledger table (paginated, append-only history)
 *   5. Request Payout dialog (min ₹5,000)
 */
import React, { useEffect, useMemo, useState } from 'react';
import CustomerShell from '../../components/customer/CustomerShell';
import { useCustomerAuth } from '../../App';
import { promoterApi } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Wallet, Copy, Share2, BadgeIndianRupee, Hourglass, Lock, TrendingUp,
  Users, Clock, ArrowDownRight, ArrowUpRight, ChevronLeft, ChevronRight,
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';

const PAGE_SIZE = 25;

const STATUS_PILL = {
  pending:   { label: 'Pending',   cls: 'bg-[#3D2E0F] text-[#E6B450] border-[#5A4519]' },
  available: { label: 'Available', cls: 'bg-[#0E2E1E] text-[#2FBF71] border-[#1F4D33]' },
  locked:    { label: 'Locked',    cls: 'bg-[#2A1F3D] text-[#B084EB] border-[#3F2F5A]' },
  paid:      { label: 'Paid',      cls: 'bg-[#1A1A1A] text-[#B9B9B9] border-[#2A2A2A]' },
  cancelled: { label: 'Cancelled', cls: 'bg-[#3D1A1A] text-[#E66666] border-[#5A2E2E]' },
};

const TYPE_LABEL = {
  commission: 'Commission',
  adjustment: 'Refund Adj.',
  payout:     'Payout',
  reversal:   'Reversal',
};

function KPICard({ icon: Icon, label, value, tone = 'gold' }) {
  const tones = {
    gold:   'text-[#D4A843]',
    green:  'text-[#2FBF71]',
    purple: 'text-[#B084EB]',
    silver: 'text-[#B9B9B9]',
  };
  return (
    <div className="surface-card card-highlight border border-[#2A2A2A] rounded-xl p-4 sm:p-5 flex-1 min-w-[150px]" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-2 text-[#B9B9B9] text-xs uppercase tracking-wider mb-2">
        <Icon size={14} className={tones[tone]} />
        <span>{label}</span>
      </div>
      <div className="font-['EB_Garamond'] text-2xl sm:text-3xl font-semibold text-[#F2F2F2]">
        {value}
      </div>
    </div>
  );
}

function LedgerRow({ row }) {
  const isPositive = row.amount > 0;
  const pill = STATUS_PILL[row.status] || STATUS_PILL.pending;
  return (
    <tr className="border-b border-[#2A2A2A] hover:bg-[#181818] transition-colors">
      <td className="py-3 px-2 text-xs text-[#B9B9B9]">
        {row.created_at ? new Date(row.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}
      </td>
      <td className="py-3 px-2 text-sm text-[#F2F2F2]">
        <div className="flex items-center gap-2">
          {isPositive ? <ArrowUpRight size={14} className="text-[#2FBF71]" /> : <ArrowDownRight size={14} className="text-[#E66666]" />}
          {TYPE_LABEL[row.type] || row.type}
        </div>
        {row.note && <div className="text-[10px] text-[#6F6F6F] mt-0.5">{row.note}</div>}
      </td>
      <td className="py-3 px-2 text-xs text-[#6F6F6F] hidden sm:table-cell">
        {row.reference_date || '—'}
      </td>
      <td className="py-3 px-2 text-right">
        <span className={`text-sm font-semibold ${isPositive ? 'text-[#2FBF71]' : 'text-[#E66666]'}`}>
          {isPositive ? '+' : ''}{formatCurrency(row.amount)}
        </span>
      </td>
      <td className="py-3 px-2 text-right">
        <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${pill.cls}`}>
          {pill.label}
        </span>
      </td>
    </tr>
  );
}

function NotPromoter() {
  return (
    <div className="max-w-md mx-auto text-center py-20 px-6">
      <div className="w-16 h-16 rounded-full bg-[#D4A843]/10 border border-[#D4A843]/30 flex items-center justify-center mx-auto mb-4">
        <Wallet className="text-[#D4A843]" size={28} />
      </div>
      <h2 className="font-['EB_Garamond'] text-3xl text-[#F2F2F2] mb-3">Become a Promoter</h2>
      <p className="text-[#B9B9B9] text-sm leading-relaxed mb-6">
        Earn commission on every booking made through your unique link. Reach out to the Scratch
        team to get onboarded — KYC and a quick NDA, that&apos;s it.
      </p>
      <Button
        className="bg-[#D4A843] hover:bg-[#E0B450] text-[#120F08] font-medium"
        onClick={() => window.location.href = 'mailto:promoters@scratchjsr.com?subject=Promoter Application'}
        data-testid="apply-promoter-cta"
      >
        Apply
      </Button>
    </div>
  );
}

export default function PromoterDashboardPage() {
  const { customer } = useCustomerAuth();
  const [loading, setLoading] = useState(true);
  const [notPromoter, setNotPromoter] = useState(false);
  const [stats, setStats] = useState(null);
  const [ledger, setLedger] = useState({ rows: [], total: 0 });
  const [page, setPage] = useState(0);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutSubmitting, setPayoutSubmitting] = useState(false);

  const refresh = async () => {
    try {
      const [statsRes, ledgerRes] = await Promise.all([
        promoterApi.stats(),
        promoterApi.ledger({ limit: PAGE_SIZE, skip: page * PAGE_SIZE }),
      ]);
      setStats(statsRes.data);
      setLedger(ledgerRes.data);
    } catch (err) {
      if (err?.response?.status === 404) {
        setNotPromoter(true);
      } else {
        toast.error('Failed to load promoter data');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [page]);

  // Prefer the short numeric code for sharing (easier to remember + dictate verbally).
  // Falls back to the long PRM-XXXXX if a legacy promoter somehow lacks short_code.
  const refCodeForUrl = stats?.promoter?.short_code || stats?.promoter?.code || '';
  const shareUrl = `${window.location.origin}/events?ref=${refCodeForUrl}`;
  const totalPages = Math.max(1, Math.ceil((ledger.total || 0) / PAGE_SIZE));

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Could not copy — long-press to copy manually');
    }
  };

  const submitPayout = async () => {
    const amt = parseInt(payoutAmount, 10);
    if (!amt || amt < 5000) { toast.error('Minimum payout is ₹5,000'); return; }
    if (amt > (stats?.balances?.available || 0)) { toast.error('Amount exceeds available balance'); return; }
    setPayoutSubmitting(true);
    try {
      await promoterApi.requestPayout(amt);
      toast.success(`Payout request for ₹${amt.toLocaleString('en-IN')} submitted`);
      setPayoutOpen(false);
      setPayoutAmount('');
      await refresh();
    } catch (err) {
      const msg = err?.response?.data?.detail?.message
        || err?.response?.data?.detail
        || 'Payout request failed';
      toast.error(typeof msg === 'string' ? msg : 'Payout request failed');
    } finally {
      setPayoutSubmitting(false);
    }
  };

  const chartData = useMemo(() => {
    return (stats?.trend_30d || []).map((p) => ({
      date:       p.date.slice(5),
      revenue:    p.revenue,
      commission: p.commission,
    }));
  }, [stats]);

  if (notPromoter) {
    return <CustomerShell title="Promoter"><NotPromoter /></CustomerShell>;
  }

  if (loading || !stats) {
    return (
      <CustomerShell title="Promoter">
        <div className="space-y-4 max-w-5xl mx-auto">
          <Skeleton className="h-24 w-full bg-[#1A1A1A]" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-28 bg-[#1A1A1A]" />)}
          </div>
          <Skeleton className="h-72 w-full bg-[#1A1A1A]" />
        </div>
      </CustomerShell>
    );
  }

  const p = stats.promoter;
  const b = stats.balances;

  return (
    <CustomerShell title="Promoter">
      <div className="max-w-5xl mx-auto space-y-6 pb-12">

        {/* Header / status pill */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs text-[#B9B9B9] uppercase tracking-wider">Your promoter code</p>
            <div className="flex items-baseline gap-4 mt-1 flex-wrap">
              <h1
                className="font-['EB_Garamond'] text-5xl sm:text-6xl text-[#D4A843] font-semibold leading-none tracking-tight tabular-nums"
                data-testid="promoter-short-code"
                title="Share this 3-digit code verbally"
              >
                {p.short_code || '—'}
              </h1>
              <div className="flex flex-col">
                <code
                  className="text-[10px] sm:text-xs uppercase text-[#6F6F6F] tracking-[2px] font-mono"
                  data-testid="promoter-long-code"
                >
                  {p.code}
                </code>
                <span className={`mt-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border self-start ${
                  p.status === 'active'
                    ? 'bg-[#0E2E1E] text-[#2FBF71] border-[#1F4D33]'
                    : p.status === 'suspended'
                      ? 'bg-[#3D1A1A] text-[#E66666] border-[#5A2E2E]'
                      : 'bg-[#3D2E0F] text-[#E6B450] border-[#5A4519]'
                }`}>
                  {p.status}
                </span>
              </div>
            </div>
            <p className="text-[11px] text-[#6F6F6F] mt-2">
              Share <span className="text-[#D4A843] font-semibold">{p.short_code || '—'}</span> verbally; the long code is for our records.
            </p>
          </div>
          <Button
            disabled={b.available < 5000 || p.status !== 'active'}
            onClick={() => setPayoutOpen(true)}
            className="bg-[#D4A843] hover:bg-[#E0B450] text-[#120F08] font-medium disabled:opacity-40"
            data-testid="request-payout-button"
          >
            <BadgeIndianRupee size={16} className="mr-2" /> Request Payout
          </Button>
        </div>

        {/* Share link */}
        <div className="surface-card card-highlight border border-[#2A2A2A] rounded-xl p-4 sm:p-5">
          <p className="text-xs text-[#B9B9B9] uppercase tracking-wider mb-2">Share this link</p>
          <div className="flex items-center gap-2 flex-wrap">
            <code className="flex-1 min-w-0 text-xs sm:text-sm bg-[#0A0A0A] border border-[#2A2A2A] rounded px-3 py-2 truncate text-[#F2F2F2] font-mono" data-testid="share-url">
              {shareUrl}
            </code>
            <Button size="sm" variant="outline" onClick={copyLink} className="border-[#2A2A2A] text-[#F2F2F2] hover:bg-[#1A1A1A]" data-testid="copy-share-url">
              <Copy size={14} className="mr-1.5" /> Copy
            </Button>
            {navigator.share && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigator.share({ url: shareUrl, title: 'Book on Scratch', text: `Use my code ${p.short_code || p.code} for events` }).catch(() => {})}
                className="border-[#2A2A2A] text-[#F2F2F2] hover:bg-[#1A1A1A]"
                data-testid="share-share-url"
              >
                <Share2 size={14} className="mr-1.5" /> Share
              </Button>
            )}
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <KPICard icon={Wallet}    label="Available" value={formatCurrency(b.available)} tone="green" />
          <KPICard icon={Hourglass} label="Pending"   value={formatCurrency(b.pending)}   tone="gold" />
          <KPICard icon={Lock}      label="Locked"    value={formatCurrency(b.locked)}    tone="purple" />
          <KPICard icon={TrendingUp} label="Lifetime" value={formatCurrency(b.lifetime)} tone="silver" />
          <KPICard icon={Users}     label="Guests"    value={stats.total_guests || 0}     tone="silver" />
        </div>

        {/* Chart */}
        <div className="surface-card card-highlight border border-[#2A2A2A] rounded-xl p-4 sm:p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[#F2F2F2] font-medium">Last 30 days</h3>
            <span className="text-[10px] text-[#6F6F6F]">Revenue vs commission</span>
          </div>
          {chartData.length === 0 ? (
            <div className="text-center py-10 text-[#6F6F6F] text-sm">
              No commission events in the last 30 days.
            </div>
          ) : (
            <div style={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2A2A2A" />
                  <XAxis dataKey="date" stroke="#6F6F6F" fontSize={11} />
                  <YAxis stroke="#6F6F6F" fontSize={11} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#111', border: '1px solid #2A2A2A', borderRadius: 8 }}
                    labelStyle={{ color: '#F2F2F2' }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11, color: '#B9B9B9' }} />
                  <Line type="monotone" dataKey="revenue"    stroke="#4AA3DF" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="commission" stroke="#D4A843" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Ledger */}
        <div className="surface-card card-highlight border border-[#2A2A2A] rounded-xl overflow-hidden">
          <div className="px-4 sm:px-5 py-4 border-b border-[#2A2A2A] flex items-center justify-between">
            <div>
              <h3 className="text-[#F2F2F2] font-medium">Wallet Ledger</h3>
              <p className="text-[10px] text-[#6F6F6F] mt-0.5">Append-only · {ledger.total} entries</p>
            </div>
            <Clock size={14} className="text-[#6F6F6F]" />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#2A2A2A] text-[10px] uppercase tracking-wider text-[#6F6F6F]">
                  <th className="py-2 px-2 text-left">Date</th>
                  <th className="py-2 px-2 text-left">Type</th>
                  <th className="py-2 px-2 text-left hidden sm:table-cell">Event Date</th>
                  <th className="py-2 px-2 text-right">Amount</th>
                  <th className="py-2 px-2 text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {ledger.rows.length === 0 && (
                  <tr><td colSpan="5" className="text-center py-10 text-[#6F6F6F] text-sm">No transactions yet</td></tr>
                )}
                {ledger.rows.map((r) => <LedgerRow key={r._id} row={r} />)}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-[#2A2A2A] flex items-center justify-between text-xs text-[#B9B9B9]">
              <span>Page {page + 1} / {totalPages}</span>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="text-[#B9B9B9] hover:text-[#F2F2F2]">
                  <ChevronLeft size={14} />
                </Button>
                <Button size="sm" variant="ghost" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)} className="text-[#B9B9B9] hover:text-[#F2F2F2]">
                  <ChevronRight size={14} />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Payout dialog */}
      <Dialog open={payoutOpen} onOpenChange={setPayoutOpen}>
        <DialogContent className="bg-[#111111] border-[#2A2A2A]">
          <DialogHeader>
            <DialogTitle className="text-[#F2F2F2]">Request Payout</DialogTitle>
            <DialogDescription className="text-[#B9B9B9]">
              Available balance: <span className="text-[#2FBF71] font-semibold">{formatCurrency(b.available)}</span>. Minimum ₹5,000.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              type="number"
              min="5000"
              step="100"
              placeholder="Amount in ₹"
              value={payoutAmount}
              onChange={(e) => setPayoutAmount(e.target.value)}
              className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2F2F2]"
              data-testid="payout-amount-input"
            />
            <Button
              className="w-full bg-[#D4A843] hover:bg-[#E0B450] text-[#120F08] font-medium"
              onClick={submitPayout}
              disabled={payoutSubmitting}
              data-testid="payout-submit-button"
            >
              {payoutSubmitting ? 'Submitting...' : `Request ₹${(parseInt(payoutAmount, 10) || 0).toLocaleString('en-IN')}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </CustomerShell>
  );
}
